import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CourseRow {
  readonly courseId: string;
  readonly courseCode: string;
  readonly courseName: string;
  readonly category: string;
  readonly contentType: string;
  readonly durationMinutes: number;
  readonly isCompliance: boolean;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED', 'ARCHIVED', 'RETIRED', 'OBSOLETE'];
const CATEGORIES = ['PROCESS', 'PRODUCT', 'POLICY', 'TECHNICAL', 'COMPLIANCE', 'SALES', 'FINANCE', 'HR', 'OPERATIONS', 'CUSTOMER', 'TRAINING', 'GENERAL'];
const CONTENT_TYPES = ['DOCUMENT', 'VIDEO', 'SCORM', 'QUIZ', 'SLIDE', 'LINK', 'INTERACTIVE', 'WEBINAR'];
const COMPLIANCE_FILTER = ['', 'true', 'false'];

/**
 * M35 — Course Management Screen (§33). Catalogue of learning courses (T469)
 * across the 12 knowledge categories and 8 delivery formats, carrying the §8
 * lifecycle and the compliance-course flag. Authors register a new course
 * (DRAFT) with its name, category, delivery format, duration and compliance
 * flag; the list is filterable by category, status, compliance and free-text
 * search. Tenant isolation is enforced server-side (LAW 4).
 */
export function CourseManagementScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CourseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [isCompliance, setIsCompliance] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);
  const [newContentType, setNewContentType] = useState(CONTENT_TYPES[0]);
  const [newDuration, setNewDuration] = useState('');
  const [newCompliance, setNewCompliance] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<CourseRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    if (isCompliance) params.set('isCompliance', isCompliance);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: CourseRow[]; total: number }>(`/v1/kms/courses${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, category, status, isCompliance, q]);

  useEffect(() => load(), [load]);

  const publish = async (r: CourseRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/courses/${r.courseId}/publish`, { method: 'POST', token });
      setNotice(t('m35.common.notice', { code: r.courseCode, label: t('m35.common.act.published') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm: 'COURSE_MANAGE' }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: CourseRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/courses/${r.courseId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.courseCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.course.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!newName.trim()) {
      setActionError(t('m35.course.nameRequired'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: CourseRow }>(`/v1/kms/courses`, {
        method: 'POST',
        body: {
          courseName: newName.trim(),
          category: newCategory,
          contentType: newContentType,
          durationMinutes: newDuration ? Number(newDuration) : undefined,
          isCompliance: newCompliance,
        },
        token,
      });
      setCreated(r.data);
      setNewName('');
      setNewDuration('');
      setNewCompliance(false);
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.course.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="course-management">
      <h1>{t('m35.course.title')}</h1>

      <form aria-label="course-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.course.courseName')}
          <input aria-label="field-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </label>
        <label>
          {t('m35.common.category')}
          <select aria-label="field-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m35.course.contentType')}
          <select aria-label="field-contenttype" value={newContentType} onChange={(e) => setNewContentType(e.target.value)}>
            {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m35.course.duration')}
          <input aria-label="field-duration" type="number" value={newDuration} onChange={(e) => setNewDuration(e.target.value)} />
        </label>
        <label>
          {t('m35.course.complianceCourse')}
          <input aria-label="field-compliance" type="checkbox" checked={newCompliance} onChange={(e) => setNewCompliance(e.target.checked)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-course" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.course.createBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="course-action-error">{actionError}</p>}
      {notice && <p aria-label="course-action-notice">{notice}</p>}
      {created && (
        <dl role="status" aria-label="course-result">
          <dt>{t('m35.course.resultCode')}</dt><dd aria-label="result-code">{created.courseCode}</dd>
          <dt>{t('m35.course.resultName')}</dt><dd aria-label="result-name">{created.courseName}</dd>
          <dt>{t('m35.common.status')}</dt><dd aria-label="result-status">{created.status}</dd>
        </dl>
      )}

      <div className="filters">
        <label>
          {t('m35.common.category')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {['', ...CATEGORIES].map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.course.compliance')}
          <select aria-label="filter-compliance" value={isCompliance} onChange={(e) => setIsCompliance(e.target.value)}>
            {COMPLIANCE_FILTER.map((s) => <option key={s || 'all'} value={s}>{s === '' ? t('m35.common.all') : s === 'true' ? t('m35.common.yes') : t('m35.common.no')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.course.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="course-count">{t('m35.course.count', { n: total })}</p>
          <DataTable<CourseRow>
            ariaLabel="course-table"
            testId="course-table"
            rowKey={(r) => r.courseId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-category': r.category })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.courseCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.courseName },
              { key: 'category', header: t('m35.common.category'), render: (r) => r.category },
              { key: 'content', header: t('m35.course.colContent'), render: (r) => r.contentType },
              { key: 'duration', header: t('m35.course.colDuration'), render: (r) => r.durationMinutes, cellAriaLabel: (r) => `course-duration-${r.courseCode}` },
              { key: 'compliance', header: t('m35.course.compliance'), render: (r) => r.isCompliance ? t('m35.common.yes') : t('m35.common.no') },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.status !== 'PUBLISHED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.courseCode}`} disabled={busy} onClick={() => void publish(r)}>{t('m35.common.btnPublish')}</Button>
                  )}
                  <Button variant="danger" type="button" aria-label={`delete-${r.courseCode}`} disabled={busy} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
